<template>
  <header class="header" :class="{ 'header--scrolled': isScrolled }">
    <div class="header__container">
      <div class="logo">
        <router-link to="/">
          <img 
            :src="logo" 
            alt="turnKey Logo" 
            width="200"
            height="50"
          />
        </router-link>
      </div>
      <div class="header__nav-wrapper">
        <button 
          class="burger-menu"
          :class="{ 'burger-menu--open': isMobileMenuOpen }"
          @click="toggleMobileMenu"
          :aria-expanded="isMobileMenuOpen"
          aria-label="Toggle navigation menu"
        >
          <span class="burger-line"></span>
          <span class="burger-line"></span>
          <span class="burger-line"></span>
        </button>
        <nav class="nav" :class="{ 'nav--mobile-open': isMobileMenuOpen }">
          <template v-for="item in navigation.mainNav.filter(item => item.menu !== false)" :key="item.path">
            <template v-if="item.children">
              <div 
                class="nav-link nav-link--has-dropdown"
                @mouseenter="!isMobileMenuOpen && openDropdown"
                @mouseleave="!isMobileMenuOpen && closeDropdown"
              >
                <div 
                  class="nav-link-content"
                  @click.stop="toggleDropdown"
                  @keydown.enter.prevent="toggleDropdown"
                  @keydown.space.prevent="toggleDropdown"
                  @keydown.escape="closeDropdown"
                  :aria-expanded="isDropdownOpen"
                  :aria-controls="`dropdown-${item.path}`"
                  role="button"
                  tabindex="0"
                >
                  {{ item.name }}
                  <span class="dropdown-icon">▼</span>
                </div>
                <div class="dropdown">
                  <div 
                    :id="`dropdown-${item.path}`"
                    class="dropdown-menu"
                    :class="{ show: isDropdownOpen }"
                    @focusout="handleFocusOut"
                  >
                    <router-link
                      v-for="child in item.children"
                      :key="child.path"
                      :to="`${item.path}/${child.path}`"
                      class="dropdown-item"
                      @click="closeDropdown"
                    >
                      {{ child.name }}
                    </router-link>
                  </div>
                </div>
              </div>
            </template>
            <router-link 
              v-else
              :to="item.path"
              class="nav-link"
              active-class="active"
              @click="closeMobileMenu"
            >
              {{ item.name }}
            </router-link>
          </template>
        </nav>
      </div>
    </div>
  </header>
</template>

<script>
import logo from '../assets/images/turnKeyRM_logo_long.svg'
import { navigation } from '@/router';
import { ref, onMounted, onUnmounted } from 'vue';

export default {
  name: 'Header',
  setup() {
    const isDropdownOpen = ref(false);
    const isMobileMenuOpen = ref(false);
    const isScrolled = ref(false);
    let lastScrollY = 0;
    let ticking = false;

    const handleScroll = () => {
      lastScrollY = window.scrollY;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Add a small threshold to prevent rapid toggling
          const threshold = 45;
          const currentScroll = lastScrollY;
          
          if (currentScroll > 60 + threshold) {
            isScrolled.value = true;
          } else if (currentScroll < 60 - threshold) {
            isScrolled.value = false;
          }
          
          ticking = false;
        });

        ticking = true;
      }
    };

    onMounted(() => {
      window.addEventListener('scroll', handleScroll, { passive: true });
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll);
    });

    const openDropdown = () => {
      isDropdownOpen.value = true;
    };

    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };

    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value;
    };

    const closeMobileMenu = () => {
      setTimeout(() => {
        isMobileMenuOpen.value = false;
      }, 300);
    };

    const handleFocusOut = (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        closeDropdown();
      }
    };

    return {
      logo,
      navigation,
      isDropdownOpen,
      isMobileMenuOpen,
      isScrolled,
      openDropdown,
      closeDropdown,
      toggleDropdown,
      toggleMobileMenu,
      closeMobileMenu,
      handleFocusOut
    };
  }
}
</script>

<style lang="scss" scoped>
/* sass:suppress mixed-decls */
@use '@/assets/scss/abstracts/variables' as *;
@use '@/assets/scss/abstracts/mixins' as *;
@use 'sass:color';


// Header base styles
.header {
  padding: $spacing-md $spacing-lg;
  @include media-down(md) {
    padding: 0px $spacing-md;
  }
  background: $color-white;
  //border-bottom: 4px solid $blue-dark;
  border-bottom: 4px solid $blue-bright;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: $z-index-sticky;
  transition: all 0.3s ease-in-out;
  &__container {
    max-width: $container-width;
    margin: 0 auto;
    padding: $spacing-md $spacing-lg;
    
    
    display: flex;
    justify-content: space-between;
    align-items: end;
    width: 100%;
    transition: all 0.3s ease-in-out;

    @include media-down(md) {
      flex-direction: column;
      padding: $spacing-md;
      padding-bottom: 0px;
    }
  }

  &--scrolled {
  @include media-up(md) {
    .header__container {
      padding: 0 $spacing-lg;
    }

    .logo img {
      height: 60px !important;
    }

    .header__nav-wrapper {
      margin-bottom: -5px !important;
    }

    .nav {
      .nav-link {
        padding: 0.25rem 1rem;
      }
    }
  }
  }

  &__nav-wrapper {
    display: flex;
    align-items: center;
    gap: $spacing-md;

    @include media-down(md) {
      width: 100%;
      
        justify-content: flex-end;
        flex-wrap: nowrap;
        flex-direction: column;
        align-items: end;
    }
  }

  // Logo styles
  .logo {
    align-self: center;
    img {
      height: 80px;
      width: 100%;
      max-width: 500px;
      margin:-1rem;
      padding:1rem;
      box-sizing: content-box;
      transition: all 0.3s ease-in-out;
    }
    
    a {
      &:hover, &:focus {
       img {
        background-color: color.adjust($blue-bright, $lightness: 40%);
       }  
      }
    }
  }


} 




// Navigation base styles
.nav {
  
  display: flex;
  gap: $spacing-md;
  align-items: center;
  justify-self: stretch;
  align-self: stretch;
  align-items: flex-end;
  margin-bottom: 5px;

  


  // Navigation links
  .nav-link {
    @include button-base;
    
    text-decoration: none;
    padding: $spacing-sm $spacing-md;
    
    transition: background-color $transition-base;
    transition-duration: 1s;

    // Trying orange theme

    color: $blue-dark;
    border-radius: 0;
    border-bottom: 3px solid $blue-bright;

    @include proxima-bold;


    &:hover {
      color: $blue-dark;
      border-bottom-color: $blue-dark;
      //border-color: $blue-bright;
      background-color: color.adjust($blue-bright, $lightness: 40%);
      //border-color: $blue-dark;
      transition-duration: 0.25s;      //color: $orange;
    }
    &:focus {
      color: $blue-dark;
      border-bottom-color: $blue-bright;
      background-color: color.adjust($blue-bright, $lightness: 40%);
      //border-color: $blue-dark;
      transition-duration: 0.25s;      //color: $orange;
    }
    &.active {
      color: $blue-bright;
      &:hover {
        color: color.adjust($blue-bright, $lightness: -20%);
      }
      //color: $color-primary;
      //font-weight: 500;
    }
  }
  .nav-link--has-dropdown {
    position: relative;
    flex-wrap: wrap;
    padding:0px;
    border-bottom: 3px solid $blue-bright;
    transition: background-color $transition-base;
    transition-duration: 1s;

    &:hover {
      border-bottom-color: $blue-dark;
      background-color: color.adjust($blue-bright, $lightness: 40%);
    }

    &:focus, :focus-within {
      background-color: color.adjust($blue-bright, $lightness: 40%);
    }

    .nav-link-content {
      @include proxima-bold;
      display: flex;
      align-items: center;
      gap: $spacing-xs;
      padding: $spacing-sm $spacing-md;
      cursor: pointer;
      width: 100%;
      justify-content: space-between;
      color: $blue-dark;
      @include media-down(md) {
        justify-content: center;
      }
    }
  
    .dropdown-toggle {
      padding: 0;
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
    }
    .dropdown-icon {
      font-size: 0.75rem;
    }
  }


  .dropdown {

    //position: relative;

    .dropdown-toggle {
      display: flex;
      align-items: center;
      gap: $spacing-xs;
      cursor: pointer;
    }

    .dropdown-menu {
      position: absolute;
      top: 100%;
      left: 0;
      background: $color-white;
      border: 3px solid $blue-dark;
      box-shadow: $shadow-md;
      min-width: 200px;
      padding: $spacing-sm 0;
      display: none;

      &.show {
        display: block;
      }

      .dropdown-item {
        display: block;
        padding: $spacing-sm $spacing-md;
        color: $color-text;
        text-decoration: none;
        transition: background-color $transition-base;
        &:hover {
      border-bottom-color: $blue-dark;
      background-color: color.adjust($blue-bright, $lightness: 40%);
    }

    &:focus, :focus-within {
      background-color: color.adjust($blue-bright, $lightness: 40%);
    }

      }
    }
  }

  // Responsive styles
  @include media-down(md) {
    gap:0px;
    flex-direction: column;
    width: 100%;
    margin-top: $spacing-md;

    .nav-link {
      width: 100%;
      text-align: center;
    }

    .dropdown {
      width: 100%;

      .dropdown-menu {
        position: static;
        width: 100%;
        box-shadow: none;
      }
    }
  }
} 

// Burger menu styles
.burger-menu {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 40px;
  height: 32px;
  padding: 5px 4px 5px 4px;
  
  background: transparent;
  border: none;
  cursor: pointer;
  
  z-index: $z-index-sticky + 1;
  transition: transform 0.3s ease-in-out;

  @include media-down(md) {
    display: flex;
    margin-top: 10px;
  }

  .burger-line {
    width: 100%;
    height: 3px;
    background-color: $blue-dark;
    transition: all 0.3s ease-in-out;
    transform-origin: left center;
  }

  &--open {
    .burger-line {
      &:first-child {
        transform: rotate(45deg);
      }
      &:nth-child(2) {
        opacity: 0;
      }
      &:last-child {
        transform: rotate(-45deg);
      }
    }
  }
}

// Mobile navigation styles
@include media-down(md) {
  .nav {
    visibility: hidden;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 0px;
    margin-top: 0px;
    padding: 0px;
    border-top: 2px solid $blue-bright;
    transition: all 0.3s ease-in-out;
    opacity: 0;
    transform: translateY(-20px);
    height: 0;
    overflow: hidden;

    .nav-link {
      width: 100%;
      text-align: left;
      padding: $spacing-sm 0;
      border-bottom: 3px solid $blue-bright;
      transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
      //transform: translateX(-20px);
      opacity: 0;
      visibility: hidden;
    }

    &--mobile-open {
      visibility: visible;
      display: flex;
      opacity: 1;
      transform: translateY(0);
      height: auto;
      overflow: visible;
      padding-bottom: $spacing-md;

      .nav-link {
        //transform: translateX(0);
        opacity: 1;
        visibility: visible;
        transition-delay: 0.1s;
        
      }
    }

    .nav-link--has-dropdown {
      width: 100%;
      
      .dropdown {
        position: static;
        width: 100%;
        
        .dropdown-menu {
          position: static;
          box-shadow: none;
          padding: $spacing-sm 0;
          display: none;
          transition: all 0.3s ease-in-out;
          transform: translateY(-10px);
          opacity: 0;
          
          &.show {
            display: block;
            transform: translateY(0);
            opacity: 1;
          }
        }
      }
    }
  }
}

</style> 